import { createBrowserRouter } from 'react-router-dom';
import App from './App';

import Home from './pages/Home';
import ServicesListing from './pages/ServicesListing';
import ServiceDetail from './pages/ServiceDetail';
import LocationLanding from './pages/LocationLanding';
import Contact from './pages/Contact';
import About from './pages/About';
import Terms from './pages/Terms';
import NotFound from './pages/NotFound';
import Login from './pages/Auth/Login';

import BookingForm from './pages/Booking/BookingForm';
import OrderSummary from './pages/Booking/OrderSummary';
import ThankYou from './pages/Booking/ThankYou';
import { BookingGuard } from './components/routing/BookingGuard';

import ConsultBookingForm from './pages/Consultation/ConsultBookingForm';
import ConsultSummary from './pages/Consultation/ConsultSummary';
import ConsultThankYou from './pages/Consultation/ConsultThankYou';

import DashboardLayout from './pages/Dashboard/DashboardLayout';
import DashboardHome from './pages/Dashboard/DashboardHome';
import MyOrders from './pages/Dashboard/MyOrders';
import OrderDetail from './pages/Dashboard/OrderDetail';
import Profile from './pages/Dashboard/Profile';
import Invoices from './pages/Dashboard/Invoices';
import DocumentUploads from './pages/Dashboard/DocumentUploads';
import SupportTickets from './pages/Dashboard/SupportTickets';

import AdminLayout from './pages/Admin/AdminLayout';
import AdminLogin from './pages/Admin/AdminLogin';
import AdminDashboard from './pages/Admin/AdminDashboard';
import CustomerList from './pages/Admin/Customers/CustomerList';
import CustomerDetail from './pages/Admin/Customers/CustomerDetail';
import OrderList from './pages/Admin/Orders/OrderList';
import AdminOrderDetail from './pages/Admin/Orders/AdminOrderDetail';
import ConsultationList from './pages/Admin/Consultations/ConsultationList';
import ConsultationDetail from './pages/Admin/Consultations/ConsultationDetail';
import ServiceList from './pages/Admin/Services/ServiceList';
import ServiceEditor from './pages/Admin/Services/ServiceEditor';
import PaymentList from './pages/Admin/Payments/PaymentList';
import ContentManagement from './pages/Admin/Content/ContentManagement';
import LeadList from './pages/Admin/Leads/LeadList';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <Home /> },
      { path: 'services', element: <ServicesListing /> },
      { path: 'services/:slug', element: <ServiceDetail /> },
      { path: 'contact', element: <Contact /> },
      { path: 'about', element: <About /> },
      { path: 'terms', element: <Terms /> },
      { path: 'login', element: <Login /> },

      // Booking (pay-for-a-specific-plan) flow is currently disabled
      // per client request — see lib/bookingFlags.ts. BookingGuard
      // redirects all of these to /consult while BOOKING_ENABLED is
      // false, without deleting the underlying pages/logic.
      { path: 'booking/:slug', element: <BookingGuard><BookingForm /></BookingGuard> },
      { path: 'booking/:slug/summary', element: <BookingGuard><OrderSummary /></BookingGuard> },
      { path: 'booking/:slug/thank-you', element: <BookingGuard><ThankYou /></BookingGuard> },

      // "Talk to us before you pay" consultation flow — intentionally
      // top-level (not nested under /booking/:slug) since it isn't tied
      // to a specific service plan and doesn't require login.
      { path: 'consult', element: <ConsultBookingForm /> },
      { path: 'consult/summary', element: <ConsultSummary /> },
      { path: 'consult/thank-you', element: <ConsultThankYou /> },

      {
        path: 'dashboard',
        element: <DashboardLayout />,
        children: [
          { index: true, element: <DashboardHome /> },
          { path: 'orders', element: <MyOrders /> },
          { path: 'orders/:id', element: <OrderDetail /> },
          { path: 'invoices', element: <Invoices /> },
          { path: 'documents', element: <DocumentUploads /> },
          { path: 'support', element: <SupportTickets /> },
          { path: 'profile', element: <Profile /> },
        ],
      },

      { path: 'admin/login', element: <AdminLogin /> },
      {
        path: 'admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: 'customers', element: <CustomerList /> },
          { path: 'customers/:id', element: <CustomerDetail /> },
          { path: 'orders', element: <OrderList /> },
          { path: 'orders/:id', element: <AdminOrderDetail /> },
          { path: 'consultations', element: <ConsultationList /> },
          { path: 'consultations/:id', element: <ConsultationDetail /> },
          { path: 'services', element: <ServiceList /> },
          { path: 'services/new', element: <ServiceEditor /> },
          { path: 'services/:slug', element: <ServiceEditor /> },
          { path: 'payments', element: <PaymentList /> },
          { path: 'content', element: <ContentManagement /> },
          { path: 'leads', element: <LeadList /> },
        ],
      },

      // Location SEO landing pages (e.g. /gst-registration-purulia) are
      // bare top-level slugs per the brief's URL scheme, so they're
      // matched last as a catch-all param route — anything not matched
      // by a more specific path above falls through to this lookup,
      // and LocationLanding itself 404s if the slug isn't a real,
      // published service_locations row.
      { path: ':urlSlug', element: <LocationLanding /> },

      { path: '*', element: <NotFound /> },
    ],
  },
]);
